import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getBlogPostBySlug } from '../../utils/contentful';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import './styles.scss';
import Footerhead from '../Footerhead';
import FooterCTACard from '../FooterCTACard';
import { renderTags } from '../../utils/renderTags';
import assets from "./blogAssets";
import { getIdFromSlug, createSlug } from '../../utils/helpers';

const BlogPost = () => {
    const { titleSlug } = useParams();
    const navigate = useNavigate();
    
    const [blogPost, setBlogPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchBlogPost = async () => {
            setLoading(true);
            try {
                // Get the post ID from the slug mapping
                let postId = getIdFromSlug(titleSlug);
                
                // Use the ID to fetch the full blog post
                const post = await getBlogPostBySlug(postId);
                
                if (!post) {
                    setError('Blog post not found');
                } else {
                    setBlogPost(post);
                    
                    // Make sure the URL matches the title
                    const correctSlug = createSlug(post.fields.blogTitle);
                    if (correctSlug !== titleSlug) {
                        navigate(`/blog/${correctSlug}`, { replace: true });
                    }
                }
            } catch (err) {
                console.error('Error fetching blog post:', err);
                setError('Failed to load blog post');
            } finally {
                setLoading(false);
            }
        };

        fetchBlogPost();
    }, [titleSlug, navigate]);

    // Rich text renderer options
    const options = {
        renderNode: {
            [BLOCKS.HEADING_1]: (node, children) => <h1 className="blog-heading">{children}</h1>,
            [BLOCKS.HEADING_2]: (node, children) => <h2 className="blog-heading">{children}</h2>,
            [BLOCKS.HEADING_3]: (node, children) => <h3 className="blog-heading">{children}</h3>,
            [BLOCKS.PARAGRAPH]: (node, children) => <p className="blog-paragraph">{children}</p>,
            [BLOCKS.UL_LIST]: (node, children) => <ul>{children}</ul>,
            [BLOCKS.OL_LIST]: (node, children) => <ol>{children}</ol>,
            [BLOCKS.LIST_ITEM]: (node, children) => <li className="blog-list-item">{children}</li>,
            [BLOCKS.EMBEDDED_ASSET]: (node) => {
                const { title, description, file } = node.data.target.fields;
                const imageUrl = file?.url ? `https:${file.url}` : assets.placeholder;
                return (
                    <div className="blog-image-container">
                        <img
                            className="blog-image img-fluid"
                            src={imageUrl}
                            alt={description || title || 'Blog image'}
                        />
                        {description && <p className="blog-image-caption">{description}</p>}
                    </div>
                );
            },
            [INLINES.HYPERLINK]: (node, children) => (
                <a
                    href={node.data.uri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="blog-link"
                >
                    {children}
                </a>
            ),
        },
    };

    if (loading) {
        return (
            <div className="container mt-5 py-5 text-center">
                <div className="spinner-border mt-5" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading blog post...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container py-5 text-center">
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
                <Link to="/blog" className="btn btn-primary mt-3">Back to Blog</Link>
            </div>
        );
    }

    if (!blogPost) {
        return (
            <div className="container py-5 text-center">
                <div className="alert alert-warning" role="alert">
                    Blog post not found
                </div>
                <Link to="/blog" className="btn btn-primary mt-3">Back to Blog</Link>
            </div>
        );
    }

    const { fields, sys } = blogPost;
    const imageUrl = fields.mainImage?.fields?.file?.url ? `https:${fields.mainImage.fields.file.url}` : assets.placeholder;
    const authorImageUrl = fields.authorImage?.fields?.file?.url ? `https:${fields.authorImage.fields.file.url}` : assets.placeholder;

    return (
        <div className="wholeBody" style={{ paddingTop: '90px' }}>
            <div className="container mt-2">
                <div className="row">
                    <div className="col-12 mb-4">
                        <Link to="/blog" className="text-decoration-none">
                            ← Back to all blogs
                        </Link>
                    </div>
                    <div className="col-md-10 mb-2 mx-auto text-center">
                        <h1 className="blog-title">{fields.blogTitle}</h1>
                        <p className='blog-description'> {fields.desc} </p>
                    </div>
                    <div className="row mb-4">
                        <div className="col-12">
                            <img
                                src={imageUrl}
                                alt={fields.blogTitle}
                                className="img-fluid rounded"
                                style={{ width: '100%', maxHeight: '500px', objectFit: 'cover' }}
                            />
                        </div>
                    </div>
                    <div className="col-12 mb-4">
                        <div className="d-flex flex-wrap">
                            {renderTags(fields.tag)}
                        </div>
                    </div>
                    <div className="blog-child-details d-flex flex-column gap-2 mb-4">
                        <h3>{fields.blogTitle}</h3>
                        <div className="BCDetails">
                            <div className="">
                                <img
                                    src={authorImageUrl}
                                    alt=""
                                    className="author-img"
                                />
                                <span>{fields.authorName}</span>
                            </div>
                            <div className="break-line"></div>
                            <div className="">
                                <img src={assets.calendar} alt="" className="calendar" />
                                <span>
                                    {new Date(sys.createdAt).toLocaleDateString('en-US', {
                                        day: '2-digit',
                                        month: 'long',
                                        year: 'numeric'
                                    })}
                                </span>
                            </div>
                        </div>
                        <span> {fields.desc} </span>
                    </div>
                </div>

                <div className="blog-content">
                    {fields.content && documentToReactComponents(fields.content, options)}

                    {!fields.content && fields.desc && (
                        <div className="blog-paragraph">{fields.desc}</div>
                    )}
                </div>
            </div>

            <FooterCTACard />

            <section className="container">
                <Footerhead />
            </section>
        </div>
    );
};

export default BlogPost; 